'use client';

import { Lightbulb, HelpCircle } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React from 'react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useRepScoreStore } from '@/presentation/hooks/useStores';

import CreditTierCard from './CreditTierCard';
import ScoreFactors from './ScoreFactors';
import ScoreGauge from './ScoreGauge';
import ScoreHistory from './ScoreHistory';

interface RepScoreDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Rep Score Drawer - Shows gamification dashboard with score details.
 * Slides in from the right.
 */
const RepScoreDrawer = observer(({ open, onOpenChange }: RepScoreDrawerProps) => {
  const repScoreStore = useRepScoreStore();

  const score = repScoreStore.currentScore;
  const scoreColor = repScoreStore.getScoreColor();
  const scoreGrade = repScoreStore.getScoreGrade();
  const breakdown = repScoreStore.scoreBreakdown;
  const recentChanges = repScoreStore.recentScoreChanges;
  const pssCreditPercentage = repScoreStore.pssCreditPercentage;
  const suggestions = repScoreStore.getImprovementSuggestions();

  // Format history for display
  const formattedHistory = recentChanges.map((change: any) => ({
    date: change.date
      ? new Date(change.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        })
      : '',
    reason: change.trigger || 'Score update',
    change: change.change || 0,
  }));

  // Get the most recent change for the gauge
  const recentChange = formattedHistory[0]?.change || 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader className="mb-6">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg font-semibold">
              Your Reputation Score
            </SheetTitle>
            <div
              className="rounded-full px-3 py-1 text-sm font-semibold"
              style={{ backgroundColor: `${scoreColor}20`, color: scoreColor }}
            >
              {scoreGrade}
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-6">
          {/* Score Gauge */}
          <ScoreGauge
            score={score}
            color={scoreColor}
            change={recentChange}
          />

          {/* PSS Credit Tier */}
          <CreditTierCard currentPercentage={pssCreditPercentage} />

          {/* Tips Section */}
          {suggestions.length > 0 && (
            <div className="rounded-lg bg-amber-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Lightbulb className="size-5 text-amber-600" />
                <h3 className="font-semibold text-amber-800">Tips</h3>
              </div>
              <ul className="space-y-1 text-sm text-amber-700">
                {suggestions.slice(0, 2).map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Score Factors */}
          <ScoreFactors breakdown={breakdown} />

          {/* Score History */}
          <ScoreHistory changes={formattedHistory} />

          {/* Learn More Section */}
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <HelpCircle className="size-6 text-gray-400" />
              <div className="flex-1">
                <p className="font-medium">Don't know about Reputation Score?</p>
                <button className="text-sm text-motitoBlue hover:underline">
                  Learn more.
                </button>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
});

RepScoreDrawer.displayName = 'RepScoreDrawer';

export default RepScoreDrawer;
